import { useAdminInsurerAttentionInit, useDownloadInsurerAttentionCsv } from '@/hooks/admin';
import { AdminInsurerAttentionFilterParams } from '@/types/admin-insurer-attention';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppAdminFormLabel, AppButton, AppSelect, SearchTextField } from '../atoms';
import { Download, Search } from '../atoms/AppIcon';
import { useTheme } from '@/shared/providers/theme';

interface AdminInsurerAttentionFilterProps {
  onSearch?: (filters: AdminInsurerAttentionFilterParams) => void;
  showCsv?: boolean;
  onBack?: () => void;
}

const AdminInsurerAttentionFilter = ({
  onSearch,
  showCsv = true,
  onBack,
}: AdminInsurerAttentionFilterProps) => {
  const [searchParams] = useSearchParams();
  const { data: initData } = useAdminInsurerAttentionInit();
  const { mutate: downloadCsv, isPending: isDownloading } = useDownloadInsurerAttentionCsv();
  const { colors } = useTheme();

  const [filters, setFilters] = useState<AdminInsurerAttentionFilterParams>({
    insurer_number:    searchParams.get('insurer_number') || '',
    insurer_name:      searchParams.get('insurer_name') || '',
    insurer_name_kana: searchParams.get('insurer_name_kana') || '',
    insurer_type_id:   searchParams.get('insurer_type_id') || '',
    prefecture_id:     searchParams.get('prefecture_id') || '',
  });

  useEffect(() => {
    setFilters({
      insurer_number:    searchParams.get('insurer_number') || '',
      insurer_name:      searchParams.get('insurer_name') || '',
      insurer_name_kana: searchParams.get('insurer_name_kana') || '',
      insurer_type_id:   searchParams.get('insurer_type_id') || '',
      prefecture_id:     searchParams.get('prefecture_id') || '',
    });
  }, [searchParams]);

  const prefectureOptions = initData?.prefectures?.map(p => ({ value: p.id, label: p.name }));
  const insurerTypeOptions = initData?.insurer_types?.map(t => ({ value: t.id, label: t.name }));

  const handleChange = (key: keyof AdminInsurerAttentionFilterParams, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.currentTarget;
      onSearch?.(filters);
      requestAnimationFrame(() => target.focus());
    }
  };

  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <AppAdminFormLabel label="保険者番号" />
          <SearchTextField
            placeholder="保険者番号"
            value={filters.insurer_number}
            onChange={e => handleChange('insurer_number', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div>
          <AppAdminFormLabel label="保険者名称" />
          <SearchTextField
            placeholder="保険者名称"
            value={filters.insurer_name}
            onChange={e => handleChange('insurer_name', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div>
          <AppAdminFormLabel label="保険者名カナ" />
          <SearchTextField
            placeholder="保険者名カナ"
            value={filters.insurer_name_kana}
            onChange={e => handleChange('insurer_name_kana', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div>
          <AppAdminFormLabel label="保険種別" />
          <AppSelect
            placeholder="保険種別"
            options={insurerTypeOptions}
            style={{ width: '100%' }}
            value={filters.insurer_type_id ? Number(filters.insurer_type_id) : undefined}
            onChange={val => handleChange('insurer_type_id', val ? val.toString() : '')}
            allowClear
          />
        </div>
        <div>
          <AppAdminFormLabel label="都道府県" />
          <AppSelect
            placeholder="都道府県"
            options={prefectureOptions}
            style={{ width: '100%' }}
            value={filters.prefecture_id ? Number(filters.prefecture_id) : undefined}
            onChange={val => handleChange('prefecture_id', val ? val.toString() : '')}
            allowClear
          />
        </div>
        <div className="flex items-end gap-2">
          <AppButton onClick={() => onSearch?.(filters)} icon={<Search />} type="primary">
            検索
          </AppButton>
          {showCsv && (
            <AppButton
              onClick={() => downloadCsv(filters)}
              icon={<Download />}
              type="primary"
              style={
                {
                  backgroundColor: colors.secondary,
                  ['--action-bg-hover' as string]: colors.secondaryHover,
                } as React.CSSProperties
              }
              className="hover:!bg-[var(--action-bg-hover)]"
              disabled={isDownloading}
              loading={isDownloading}
            >
              CSV
            </AppButton>
          )}
          {onBack && (
            <AppButton onClick={onBack}>戻る</AppButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInsurerAttentionFilter;
